import React from 'react';
import { isIOS, isAndroid, isMobileDevice } from '../utils/mobileOptimizations';

interface LocationData {
  latitude: number;
  longitude: number;
  address?: string;
  suburb?: string;
  postcode?: string;
}

interface ClickableLocationProps {
  location: LocationData;
  displayText?: string;
  className?: string;
  showIcon?: boolean;
  children?: React.ReactNode;
}

/**
 * Clickable location component that opens external map applications
 * - Desktop: Opens Google Maps in browser
 * - iOS: Opens Apple Maps app
 * - Android: Opens Google Maps app
 */
export const ClickableLocation: React.FC<ClickableLocationProps> = ({
  location,
  displayText,
  className = '',
  showIcon = true,
  children
}) => {
  const generateMapUrl = (location: LocationData): string => {
    const { latitude, longitude, address, suburb, postcode } = location;
    
    // Build a comprehensive address string for better map results
    let query = '';
    
    if (address && suburb && postcode) {
      // Full address: "123 Main St, Sydney, NSW 2000"
      query = `${address}, ${suburb}, ${postcode}`;
    } else if (address && suburb) {
      // Address with suburb: "123 Main St, Sydney"
      query = `${address}, ${suburb}`;
    } else if (address) {
      // Just address
      query = address;
    } else if (suburb && postcode) {
      // Suburb and postcode: "Sydney, 2000"
      query = `${suburb}, ${postcode}`;
    } else {
      // Fallback to coordinates
      query = `${latitude},${longitude}`;
    }
    
    if (isMobileDevice()) {
      if (isIOS()) {
        // Apple Maps URL scheme
        return `maps://maps.apple.com/?q=${encodeURIComponent(query)}&ll=${latitude},${longitude}`;
      } else if (isAndroid()) {
        // Google Maps app URL scheme for Android
        return `geo:${latitude},${longitude}?q=${encodeURIComponent(query)}`;
      }
    }
    
    // Desktop or fallback: Google Maps web
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  };

  const handleLocationClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const mapUrl = generateMapUrl(location);
      
      // Try to open the map application
      if (isMobileDevice()) {
        // For mobile, try to open the native app
        window.location.href = mapUrl;
      } else {
        // For desktop, open in new tab
        window.open(mapUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Failed to open map application:', error);
      
      // Build fallback query using the same logic
      const { address, suburb, postcode } = location;
      let fallbackQuery = '';
      
      if (address && suburb && postcode) {
        fallbackQuery = `${address}, ${suburb}, ${postcode}`;
      } else if (address && suburb) {
        fallbackQuery = `${address}, ${suburb}`;
      } else if (address) {
        fallbackQuery = address;
      } else if (suburb && postcode) {
        fallbackQuery = `${suburb}, ${postcode}`;
      } else {
        fallbackQuery = `${location.latitude},${location.longitude}`;
      }
      
      // Fallback to Google Maps web version
      const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fallbackQuery)}`;
      
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getDisplayText = (): string => {
    if (displayText) return displayText;
    if (children) return '';
    
    // Generate display text from location data
    if (location.address) return location.address;
    if (location.suburb && location.postcode) {
      return `${location.suburb}, ${location.postcode}`;
    }
    
    return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
  };

  const baseClassName = `inline-flex items-center gap-1 text-urban-600 hover:text-cardboard cursor-pointer transition-colors duration-200 hover:underline ${className}`;

  return (
    <button
      onClick={handleLocationClick}
      className={baseClassName}
      title={`Open in ${isIOS() ? 'Apple Maps' : 'Google Maps'}`}
      type="button"
    >
      {showIcon && (
        <svg 
          className="w-4 h-4 flex-shrink-0" 
          fill="currentColor" 
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path 
            fillRule="evenodd" 
            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" 
            clipRule="evenodd" 
          />
        </svg>
      )}
      {children || <span className="truncate">{getDisplayText()}</span>}
    </button>
  );
};

export default ClickableLocation;